import { StandardEvents } from '@shopify/events';

// Only AR interactions go to this existing GA4 property. Shopify handles page views.
const measurementId = 'G-2C66ZH3KHH';
let analyticsConfigured = false;

export function trackAR(name, data) {
  try {
  const debug = new URLSearchParams(location.search).get('ar_debug') === '1';
  if (window.Shopify?.designMode || !window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()) {
    if (debug) console.info(`AR analytics: waiting for analytics consent; privacy API ready=${Boolean(window.Shopify?.customerPrivacy)}; theme editor=${Boolean(window.Shopify?.designMode)}`);
    return;
  }
  if (typeof window.gtag !== 'function') {
    if (debug) console.info('AR analytics: Google tag unavailable');
    return;
  }
  if (!analyticsConfigured) {
    window.gtag('config', measurementId, { send_page_view: false });
    analyticsConfigured = true;
  }
  window.gtag('event', name, {
    send_to: measurementId,
    ...data,
    ...(debug ? { debug_mode: true } : {}),
  });
  if (debug) console.info('AR analytics: event queued', name);
  } catch {
    // Analytics failures must never interrupt the customer's preview.
  }
}

export class ProductAR extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.catalog = JSON.parse(this.querySelector('[data-ar-models]').textContent);
    this.dialog = this.querySelector('dialog');
    this.viewer = this.querySelector('model-viewer');
    this.status = this.querySelector('[data-ar-status]');
    this.openButton = this.querySelector('[data-ar-open]');
    this.section = this.closest('.shopify-section');
    this.selection = 0;
    this.loadVersion = 0;
    this.openButton.addEventListener('click', () => {
      if (!this.model || this.pending) return;
      this.dialog.showModal();
      this.track('ar_preview_open');
      this.load();
    }, options);
    this.querySelector('[data-ar-close]').addEventListener('click', () => this.dialog.close(), options);
    this.dialog.addEventListener('click', event => {
      const box = this.dialog.getBoundingClientRect();
      if (event.target === this.dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) this.dialog.close();
    }, options);
    this.querySelector('[data-ar-retry]').addEventListener('click', () => this.load(true), options);
    this.querySelector('[slot="ar-button"]').addEventListener('click', () => {
      if (this.dataset.loadState === 'ready' && this.viewer.canActivateAR) this.track('ar_launch');
    }, options);
    this.section?.addEventListener('change', event => {
      if (!event.target.closest('variant-picker')) return;
      this.section.querySelectorAll('product-price').forEach(price => price.updatedCallback?.());
    }, options);
    this.section?.addEventListener(StandardEvents.productSelect, event => this.selectVariant(event), options);
    this.viewer.addEventListener('load', () => {
      if (!this.model || this.viewer.getAttribute('src') !== this.model.glb || this.pending) return;
      this.dataset.loadState = 'ready';
      this.status.textContent = this.viewer.canActivateAR
        ? 'Ready. Tap “Place in my van” to see it full-size.'
        : '3D preview ready. For camera view, open this product on an AR-compatible phone.';
      this.querySelector('[data-ar-retry]').hidden = true;
    }, options);
    this.viewer.addEventListener('error', () => this.showError('model_load'), options);
    this.viewer.addEventListener('ar-status', event => {
      if (event.detail.status === 'failed') {
        this.status.textContent = 'AR could not start. You can still explore the 3D preview, or try Safari on iPhone or Chrome on an AR-compatible Android phone.';
        this.track('ar_error', { ar_error_type: 'launch' });
      }
      // Native Quick Look / Scene Viewer do not report confirmed placement.
      if (event.detail.status === 'object-placed') this.track('ar_placed', { ar_mode: 'webxr' });
    }, options);
    this.applyVariant(this.dataset.variantId);
    if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
    }
  }
  disconnectedCallback() {
    this.selection++;
    this.loadVersion++;
    if (this.dialog?.open) this.dialog.close();
    this.controller?.abort();
    this.controller = null;
  }
  async selectVariant(event) {
    const selection = ++this.selection;
    this.pending = true;
    this.openButton.disabled = true;
    this.loadVersion++;
    if (this.dialog.open) this.dialog.close();
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || selection !== this.selection) return;
      this.pending = false;
      this.applyVariant(detail?.productId && String(detail.productId) !== this.dataset.productId ? null : detail?.resource?.id);
    } catch {
      if (selection !== this.selection) return;
      this.pending = false;
      this.applyVariant(null);
    }
  }
  applyVariant(id) {
    this.model = this.catalog.variants[String(id)];
    this.openButton.disabled = !this.model;
    this.querySelector('.vr-ar-callout').hidden = !this.model;
    if (!this.model) return;
    this.dataset.variantId = String(id);
    this.querySelectorAll('[data-ar-finish]').forEach(label => { label.textContent = this.model.label; });
    const dimensions = this.model.dimensions.map(n => Math.round(n)).join(' × ') + ' mm (W × D × H)';
    this.querySelector('[data-ar-dimensions]').textContent = dimensions;
    this.querySelector('[data-ar-model-note]').textContent = this.model.note || '';
    this.viewer.alt = `${this.dataset.productTitle}, ${this.model.label}, ${dimensions}`;
  }
  async load(retry = false) {
    if (!this.model || this.pending || (!retry && this.viewer.getAttribute('src') === this.model.glb)) return;
    const version = ++this.loadVersion;
    const model = this.model;
    this.dataset.loadState = 'loading';
    this.status.textContent = 'Loading your full-size preview…';
    this.querySelector('[data-ar-retry]').hidden = true;
    try {
      if (!customElements.get('model-viewer')) await import(this.dataset.viewerUrl);
      if (!this.isConnected || version !== this.loadVersion || model !== this.model) return;
      this.viewer.setAttribute('ios-src', model.usdz);
      if (retry) this.viewer.removeAttribute('src');
      this.viewer.setAttribute('src', model.glb);
    } catch {
      if (version === this.loadVersion) this.showError('viewer_load');
    }
  }
  showError(type) {
    this.dataset.loadState = 'error';
    this.status.textContent = 'The preview could not load. Check your connection and try again.';
    this.querySelector('[data-ar-retry]').hidden = false;
    this.track('ar_error', { ar_error_type: type });
  }
  track(name, extra = {}) {
    if (!this.model) return;
    trackAR(name, {
      ar_product: this.dataset.productTitle.slice(0, 100),
      ar_product_id: this.dataset.productId,
      ar_variant: this.model.sku.slice(0, 100),
      ar_variant_id: this.dataset.variantId,
      ar_category: this.catalog.category,
      ar_finish: this.model.label,
      ...extra,
    });
  }
}
if (!customElements.get('vr-product-ar')) customElements.define('vr-product-ar', ProductAR);
